// src/components/QuickPickUpgradeNotice.js
import React, { useState } from 'react';
import { Zap, X, Star, TrendingUp, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getBrazilianStatus } from '../utils/detectBrazil';
import { quickPickUpgradeNoticePtBR } from './mappings/quickPickUpgradeNotice.pt-br';

const QuickPickUpgradeNotice = ({ 
  onClose, 
  isMobile = false,
  sport = 'Sports'
}) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);
  
  // Check if user is Brazilian
  const isBrazilian = getBrazilianStatus();
  
  // Translation function
  const t = (text) => {
    if (!isBrazilian) return text;
    // Handle dynamic sport messages
    if (text.includes('Get More') && text.includes('Quick Picks!')) {
      const sportKey = `Get More ${sport} Quick Picks!`;
      return quickPickUpgradeNoticePtBR[sportKey] || quickPickUpgradeNoticePtBR["Get More Sports Quick Picks!"];
    }
    return quickPickUpgradeNoticePtBR[text] || text;
  };

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      setTimeout(onClose, 300); // Delay to allow exit animation to complete
    }
  };
  
  const handleBuyStarCoins = () => {
    // Navigate to upgrade page
    navigate('/upgrade');
    handleClose();
  };

  // For mobile, show a banner at the top
  if (isMobile) {
    return (
      <AnimatePresence>
        {isVisible && (
          <motion.div 
            className="fixed top-14 left-0 right-0 z-[60] px-4 py-3"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <div className="max-w-md mx-auto bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-md rounded-xl p-4 border border-emerald-500/30 shadow-xl">
              {/* Premium value banner */}
              <div className="bg-gradient-to-r from-emerald-500/20 to-violet-500/20 border border-emerald-500/30 rounded-lg px-3 py-2.5 mb-3">
                <div className="flex items-center justify-center gap-2">
                  <Target className="w-4 h-4 text-emerald-300" />
                  <p className="text-emerald-300 text-sm font-semibold">
                    {t(`Get More ${sport} Quick Picks!`)}
                  </p>
                  <TrendingUp className="w-4 h-4 text-violet-300" />
                </div>
                <p className="text-center text-xs text-white/70 mt-1">
                  {t("Each Quick Pick saves to your chat history")}
                </p>
              </div>
              
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <Zap className="w-5 h-5 text-amber-400 mr-2" />
                  <span className="text-amber-300 font-medium">{t("Quick Picks Limit Reached")}</span>
                </div>
                <button 
                  onClick={handleClose} 
                  className="text-gray-400 hover:text-gray-300 p-1 rounded hover:bg-white/5"
                  aria-label={t("Close")}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <p className="text-gray-300 text-sm mt-2">
                {t("You've used your daily Quick Picks. Get more with Star Coins!")}
              </p>

              {/* Star Coins - Single CTA button */}
              <button
                onClick={handleBuyStarCoins}
                className="w-full mt-3 bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-500 hover:to-orange-400 text-white font-bold py-2.5 px-3 text-sm rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg"
              >
                <Star className="w-4 h-4" />
                {t('Buy More Star Coins to Continue Picking')}
                <Star className="w-4 h-4" />
              </button>
              
              <div className="flex items-center justify-center mt-2 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <Zap className="w-3 h-3 text-emerald-400" />
                  {t('7 coins / quick pick')}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  // Desktop version (non-blocking notification)
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div 
          className="fixed bottom-6 right-6 z-40 max-w-md"
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
        >
          <div className="bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-sm rounded-xl p-6 border border-emerald-500/30 shadow-2xl">
            {/* Premium value banner */}
            <div className="bg-gradient-to-r from-emerald-500/20 to-violet-500/20 border border-emerald-500/30 rounded-lg px-4 py-3 mb-4">
              <div className="flex items-center justify-center gap-2">
                <Target className="w-5 h-5 text-emerald-300" />
                <p className="text-emerald-300 font-semibold">
                  {t(`Get More ${sport} Quick Picks!`)}
                </p>
                <TrendingUp className="w-5 h-5 text-violet-300" />
              </div>
              <p className="text-center text-sm text-white/70 mt-1">
                {t("AI-powered predictions saved to chat history")}
              </p>
            </div>
            
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center">
                <Zap className="w-6 h-6 text-amber-400 mr-2" />
                <h3 className="text-lg font-medium text-amber-300">
                  {t("Quick Picks Limit Reached")}
                </h3>
              </div>
              <button 
                onClick={handleClose}
                className="p-1 rounded hover:bg-white/5 text-gray-400 hover:text-gray-300 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-gray-300 mb-4">
              {t("You've reached your daily Quick Pick limit. Get instant access with Star Coins!")}
            </p>
            
            {/* Star Coins - Single CTA */}
            <div className="mb-4">
              <button
                onClick={handleBuyStarCoins}
                className="w-full bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-500 hover:to-orange-400 text-white font-bold py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg"
              >
                <Star className="w-5 h-5" />
                {t('Buy More Star Coins to Continue Picking')}
                <Star className="w-5 h-5" />
              </button>
            </div>
            
            {/* Close button only */}
            <button
              onClick={handleClose}
              className="w-full py-2 px-4 border border-emerald-400/20 text-emerald-300 hover:bg-emerald-500/10 rounded-lg transition-colors text-sm font-medium"
            >
              {t('Maybe Later')}
            </button>
            
            <div className="flex items-center justify-center mt-3 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <Zap className="w-3.5 h-3.5 text-emerald-400" />
                {t('7 coins / quick pick')}
              </span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default QuickPickUpgradeNotice;